/**
 * Class selection.
 *
 * Layout follows the Spotify Kids "Pick your character" pattern from the Mobbin
 * reference set: one large preview, a thumbnail strip to switch, and a single
 * full-width confirm. Keeps the first decision to one tap target at phone size.
 */

import { useState } from 'react';
import type { ClassId } from '@lastroll/engine';
import { getContentDb } from '@lastroll/content';
import { portraitUri } from './art.js';

const db = getContentDb();

export interface ClassSelectProps {
  onStart: (classId: ClassId, seed: string) => void;
  onLoad: () => void;
  hasSave: boolean;
}

/**
 * 새 런의 씨앗을 고른다.
 *
 * 절대 규칙 1은 `Math.random()` 을 금지한다. 게임 안의 무작위는 전부 시드에서
 * 재현 가능해야 하기 때문이다. **그 시드 자체를 뽑는 이 한 곳**만이 예외이고,
 * 여기서도 `Math.random` 은 쓰지 않는다 — 브라우저의 암호학적 난수를 쓴다.
 * 엔진 쪽 난수는 언제나 `packages/engine/src/rng.ts` 를 거친다.
 */
function randomSeed(): string {
  const words = ['ember', 'hollow', 'crown', 'ash', 'thread', 'vow', 'lantern', 'omen', 'quiet'];
  const bytes = new Uint32Array(3);
  crypto.getRandomValues(bytes);
  const pick = (i: number): string => words[bytes[i]! % words.length]!;
  return `${pick(0)}-${pick(1)}-${(bytes[2]! % 900) + 100}`;
}

export function ClassSelect({ onStart, onLoad, hasSave }: ClassSelectProps): JSX.Element {
  const [selected, setSelected] = useState<ClassId>('warrior');
  const [seed, setSeed] = useState(randomSeed);
  const cls = db.classes.find((c) => c.id === selected) ?? db.classes[0]!;

  return (
    <div className="select wrap">
      <div>
        <h1 className="select__title">THE LAST ROLL</h1>
        <p className="select__tagline">주사위를 굴리고, 선택하고, 그 결과가 세계에 남는다.</p>
      </div>

      <div className="select__hero">
        <img
          className="select__portrait"
          src={portraitUri(cls.name, cls.id, 'neutral', cls.id, false)}
          alt={`${cls.name} 초상`}
        />
        <div>
          <h2 className="select__name">{cls.name}</h2>
          <p className="muted">{cls.tagline}</p>
        </div>
        <p className="select__desc">{cls.description}</p>
        <div className="select__crit">6의 의미 · {cls.criticalAbility}</div>
      </div>

      <div className="select__strip" role="radiogroup" aria-label="클래스 선택">
        {db.classes.map((c) => (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={c.id === selected}
            className={`thumb${c.id === selected ? ' is-active' : ''}`}
            onClick={() => setSelected(c.id)}
          >
            <img src={portraitUri(c.name, c.id, 'neutral', c.id, false)} alt="" />
            <span>{c.name}</span>
          </button>
        ))}
      </div>

      <div className="seedrow">
        <label htmlFor="seed">시드</label>
        <input
          id="seed"
          value={seed}
          onChange={(e) => setSeed(e.target.value)}
          spellCheck={false}
          aria-describedby="seed-help"
        />
        <button type="button" className="iconbtn" onClick={() => setSeed(randomSeed())} aria-label="시드 새로 뽑기">
          ⟳
        </button>
      </div>
      <p id="seed-help" className="muted" style={{ textAlign: 'center', marginTop: -12 }}>
        같은 시드는 항상 같은 모험을 만든다. 친구와 같은 시드로 다른 선택을 해보라.
      </p>

      <div className="actionbar" style={{ position: 'static', background: 'none' }}>
        <button
          type="button"
          className="roll-btn"
          onClick={() => onStart(selected, seed.trim() || randomSeed())}
        >
          모험 시작
        </button>
      </div>

      {hasSave && (
        <button type="button" className="wait-btn" style={{ alignSelf: 'center' }} onClick={onLoad}>
          이어서 하기
        </button>
      )}

      {/* 개인정보처리방침 · 이용약관 — 한국어 · 영어 (public/legal). 문의 projectteamforyou@gmail.com */}
      <footer className="legal-links">
        <a href="legal/privacy-ko.html">개인정보처리방침</a>
        <a href="legal/terms-ko.html">이용약관</a>
        <a href="legal/privacy-en.html" lang="en">Privacy</a>
        <a href="legal/terms-en.html" lang="en">Terms</a>
      </footer>
    </div>
  );
}
